import type {CSS, CSSProperties} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import {useLabelledControl} from './LabelContext';
import TextInputWrapper from './TextInputWrapper';

export type Sizes = 'normal' | 'large';

const Input = styled(
  'input',
  (
    {$hasPrefix, $size = 'normal'}: {$hasPrefix: boolean; $size?: Sizes},
    designColors,
  ) => ({
    backgroundColor: 'transparent',
    border: 0,
    borderBottomLeftRadius: $hasPrefix ? 0 : 4,
    borderBottomRightRadius: 4,
    borderTopLeftRadius: $hasPrefix ? 0 : 4,
    borderTopRightRadius: 4,
    color: designColors.contentTextAccent,
    flex: '1 1',
    fontFamily: DEFAULT_FONT,
    fontSize: $size === 'normal' ? 14 : 20,
    gridRow: '1 / 1',
    height: '100%',
    letterSpacing: 'inherit',
    maxWidth: '100%',
    padding: '0 10px',
    position: 'relative',
    transition: 'box-shadow 0.2s, opacity 0.2s',
    width: '100%',
    zIndex: 2,

    // `TextInputWrapper` draws the focus ring around the whole field. The one
    // caller with `useWrapper={false}`, `TimecodeInput`, puts its fields in a
    // wrapper of its own.
    // eslint-disable-next-line no-restricted-syntax -- the wrapper draws the ring
    ':focus': {outline: 'none'},

    ':not(:last-child)': {
      borderBottomRightRadius: 0,
      borderTopRightRadius: 0,
    },

    '::placeholder': {color: designColors.contentTextMuted},
    '::-webkit-placeholder': {color: designColors.contentTextMuted},
  }),
  {className: 'TextInput', dir: 'auto'},
);

export interface Props {
  'aria-controls'?: string;
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: 'dialog' | 'listbox' | 'menu' | 'tree' | 'grid';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  className?: string;
  disabled?: boolean;
  endCapStyle?: CSS;
  id?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  inputStyle?: CSS;
  /** Marks the field invalid. A surrounding `<Label>` with an error does too. */
  invalid?: boolean;
  /** The language of the text, when it is not the language of the page (WCAG 3.1.2). */
  lang?: string;
  /** The latest value, for the `date` and `datetime-local` types. */
  max?: string;
  maxLength?: number;
  /** The earliest value, for the `date` and `datetime-local` types. */
  min?: string;
  minLength?: number;
  name?: string;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLInputElement>['onBlur'];
    onChange: React.InputHTMLAttributes<HTMLInputElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLInputElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLInputElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLInputElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLInputElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLInputElement>['onKeyPress'];
  }>;
  onChange?: (value: string) => void;
  pattern?: string;
  placeholder?: string;
  prefix?: React.ReactNode;
  readOnly?: boolean;
  required?: boolean;
  size?: Sizes;
  style?: CSSProperties;
  suffix?: React.ReactNode;
  tabIndex?: number;
  type?:
    | 'date'
    | 'datetime-local'
    | 'email'
    | 'text'
    | 'url'
    | 'password'
    | 'number'
    | 'tel'
    | 'time';
  useWrapper?: boolean;
  value: string;
}

const TextInput = (
  {
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    autoComplete,
    endCapStyle,
    id,
    inputStyle,
    invalid,
    onChange,
    nativeEvents,
    prefix,
    size = 'normal',
    style,
    suffix,
    type = 'text',
    useWrapper = true,
    value,
    ...rest
  }: Props,
  ref: React.Ref<HTMLInputElement>,
) => {
  const labelling = useLabelledControl({
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    id,
    invalid,
  });
  const {maxLength} = rest;
  const nativeOnChange = nativeEvents?.onChange;
  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement, HTMLInputElement>) => {
      nativeOnChange?.(e);
      if (!onChange) {
        return;
      }
      const value = e.target.value;
      if (maxLength && value.length > maxLength) {
        onChange(value.substr(0, maxLength));
      } else {
        onChange(value);
      }
    },
    [nativeOnChange, onChange, maxLength],
  );
  const input = (
    <Input
      autoComplete={autoComplete}
      $hasPrefix={Boolean(prefix)}
      $size={size}
      style={inputStyle}
      {...rest}
      {...labelling}
      ref={ref}
      type={type}
      {...nativeEvents}
      onChange={handleChange}
      value={value}
    />
  );
  if (!useWrapper) {
    return input;
  }
  return (
    <TextInputWrapper
      disabled={!!rest.disabled}
      endCapStyle={endCapStyle}
      invalid={Boolean(labelling['aria-invalid'])}
      prefix={prefix}
      size={size}
      style={style}
      suffix={suffix}
    >
      {input}
    </TextInputWrapper>
  );
};

export default React.forwardRef<HTMLInputElement, Props>(TextInput);
